<template>
  <PageContainer maxWidth="1300px">
    <Card class="mt-4">
      <template #content>
        <div class="grid">
          <div class="col-12 sm:col sm:flex-grow-1">
            <h2 class="mt-0 text-xl">
              Podcasts
            </h2>
          </div>
          <div class="col-12 sm:col sm:flex-grow-0">
            <Button
              v-if="myUserStore.canCreateProgram"
              class="w-full justify-content-center text-wrap-nowrap"
              severity="success"
              @click="addProgramDialogData.visible = true"
            >
              Add Show
            </Button>
          </div>
        </div>

        <div class="mt-4">
          <DataTable
            :value="compiledPrograms"
            dataKey="id"
          >
            <Column
              header="Name"
            >
              <template #body="slotProps">
                <div class="flex">
                  {{ slotProps.data.name }}
                  <template v-if="!!slotProps.data.spreaker_show_id">
                    <Chip
                      v-if="!!slotProps.data.email_confirmation_date"
                      class="ml-1 bg-green-400 text-white text-xs"
                    >
                      Verified
                    </Chip>
                    <!-- eslint-disable max-len -->
                    <Chip
                      v-else
                      class="ml-1 bg-yellow-100 text-yellow-600 text-xs"
                      v-tooltip.top="`We have sent a verification email. You must validate the podcast before you can start receiving campaigns.`"
                    >
                      Not verified
                    </Chip>
                    <!-- eslint-enable max-len -->
                  </template>
                </div>
              </template>
            </Column>
            <Column
              header="Voice Profile"
            >
              <template #body="slotProps">
                {{
                  userToProgramDictionary[slotProps.data.id]
                    ? userToProgramDictionary[slotProps.data.id].fullName
                    : ''
                }}
                <template v-if="!slotProps.data.host_user_id">
                  <i
                    class="ml-1 pi pi-exclamation-triangle text-yellow-500"
                    :style="{
                      fontSize: '1.25em',
                    }"
                    v-tooltip.top="`You must add a voice profile before receiving ad campaigns.`"
                  />
                </template>
                  <template v-else-if="userToProgramDictionary[slotProps.data.id]
                  && !userToProgramDictionary[slotProps.data.id].voice_id">
                  <!-- eslint-disable max-len -->
                  <i
                    class="ml-1 pi pi-exclamation-triangle text-yellow-500"
                    :style="{
                      fontSize: '1.25em',
                    }"
                    v-tooltip.top="'The voice profile assigned to this podcast does not have a voice configured. You must configure a voice before the podcast can receive ad campaigns.'"
                  />
                  <!-- eslint-enable max-len -->
                  </template>
              </template>
            </Column>
            <Column
              class="w-1p"
            >
              <template #body="slotProps">
                <Button
                  icon="pi pi-ellipsis-v"
                  text
                  plain
                  aria-haspopup="true"
                  :aria-controls="`programs-row-menu-${slotProps.data.id}`"
                  @click="(event) => this.$refs[`programRowMenu${slotProps.data.id}`].toggle(event)"
                />
                <Menu
                  :id="`programs-row-menu-${slotProps.data.id}`"
                  class="right-align-menu"
                  :ref="`programRowMenu${slotProps.data.id}`"
                  popup
                  :model="programRowOptionsDictionary[slotProps.data.id]"
                />
              </template>
            </Column>
            <template #empty>No podcasts found</template>
          </DataTable>
        </div>
      </template>
    </Card>

    <!-- program dialogs -->
    <ProgramFormDialog
      v-model:visible="addProgramDialogData.visible"
      :isSubmitting="addProgramDialogData.isSubmitting"
      :categoryOptions="categoriesStore.categoryOptions"
      :sensitiveCategoryOptions="categoriesStore.sensitiveCategoryOptions"
      :hostUserOptions="hostUserOptions"
      :canEditHostUserId="myUserStore.canEditProgramHostUser"
      @submit="onSubmitAddProgram"
    />

    <ProgramFormDialog
      v-model:visible="editProgramDialogData.visible"
      :isSubmitting="editProgramDialogData.isSubmitting"
      :categoryOptions="categoriesStore.categoryOptions"
      :sensitiveCategoryOptions="categoriesStore.sensitiveCategoryOptions"
      :program="editProgramDialogData.program"
      :hostUserOptions="hostUserOptions"
      :canEditHostUserId="myUserStore.canEditProgramHostUser"
      @submit="onSubmitEditProgram"
    />

    <ConfirmDialog
      v-model:visible="deleteProgramDialogData.visible"
      header="Confirm Delete"
      :isSubmitting="deleteProgramDialogData.isSubmitting"
      @confirm="onSubmitDeleteProgram"
    >
      <div v-html="deleteProgramDialogData.body" />
    </ConfirmDialog>
  </PageContainer>
</template>

<script>
import { mapStores } from 'pinia';
import {
  useMyUserStore,
  useUsersStore,
  useProgramsStore,
  useCategoriesStore,
} from '@/stores';
import ConfirmDialog from '@/components/confirmDialog';
import ProgramFormDialog from '@/components/programFormDialog';
import { parseMessageFromError } from '@/utils/errors';
import * as api from '@/api';

export default {
  components: {
    ConfirmDialog,
    ProgramFormDialog,
  },
  computed: {
    ...mapStores(
      useMyUserStore,
      useUsersStore,
      useProgramsStore,
      useCategoriesStore,
    ),
    compiledPrograms() {
      const programs = this.myUserStore.myUser && this.myUserStore.myUser.organization_id
        ? this.programsStore.getProgramsByOrganizationId(this.myUserStore.myUser.organization_id)
        : [];

      return programs;
    },
    programRowOptionsDictionary() {
      return this.compiledPrograms.reduce((acc, item) => {
        acc[item.id] = [
          {
            label: 'Edit',
            command: () => this.onEditProgram(item),
          },
          {
            label: 'Delete',
            command: () => this.onDeleteProgram(item),
            visible: () => this.myUserStore.canDeleteProgram,
          },
        ];

        return acc;
      }, {});
    },
    hostUserOptions() {
      return this.myUserStore.myUser && this.myUserStore.myUser.organization_id
        ? this.usersStore.getUserOptionsByOrganizationId(this.myUserStore.myUser.organization_id)
        : [];
    },
    userToProgramDictionary() {
      const users = this.myUserStore.myUser && this.myUserStore.myUser.organization_id
        ? this.usersStore.getUsersByOrganizationId(this.myUserStore.myUser.organization_id)
        : [];

      return this.compiledPrograms.reduce((acc, program) => {
        const matchingUser = program.host_user_id
          ? users.find((user) => Number(user.id) === Number(program.host_user_id))
          : undefined;

        if (matchingUser) {
          matchingUser.fullName = `${matchingUser.fname} ${matchingUser.lname}`.trim();
        }

        acc[program.id] = matchingUser;

        return acc;
      }, {});
    },
  },
  data() {
    return {
      addProgramDialogData: {
        visible: false,
        isSubmitting: false,
      },
      editProgramDialogData: {
        visible: false,
        isSubmitting: false,
        program: null,
      },
      deleteProgramDialogData: {
        visible: false,
        isSubmitting: false,
        program: null,
      },
    };
  },
  mounted() {
    this.getOrganizationsUsers();
    this.getOrganizationsPrograms();
  },
  methods: {
    getOrganizationsUsers() {
      return this.usersStore.getUsers({
        organizationId: this.myUserStore.myOrganization
          ? this.myUserStore.myOrganization.id
          : undefined,
      });
    },
    getOrganizationsPrograms() {
      return this.programsStore.getPrograms({
        organizationId: this.myUserStore.myOrganization
          ? this.myUserStore.myOrganization.id
          : undefined,
      });
    },
    onEditProgram(program) {
      this.editProgramDialogData = {
        ...this.editProgramDialogData,
        visible: true,
        program,
      };
    },
    async onSubmitEditProgram({ program, programForm }) {
      try {
        this.editProgramDialogData.isSubmitting = true;

        const targetAgeRanges = programForm.targetAgeRanges.reduce((acc, item) => {
          acc[item] = true;

          return acc;
        }, {});

        await api.updateProgram({
          programId: program.id,
          name: programForm.name,
          description: programForm.showDescription,
          contactName: programForm.hostName,
          contactEmail: programForm.hostEmail,
          averageListeners: programForm.averageMonthlyDownloads,
          tapProgramId: programForm.tapProgramId,
          spreakerShowId: programForm.spreakerShowId,
          targetGender: programForm.targetGender,
          targetAgeRanges,
          adPreferences: programForm.adPreferences,
          categories: programForm.categories,
          blacklistedCategories: programForm.categoriesBlacklist,
          approvalBehavior: programForm.approvalBehavior,
          rssUrl: programForm.rssUrl,
          containsSensitiveContent: programForm.containsSensitiveContent,
          hostUserId: programForm.hostUserId,
          hostingPlatform: programForm.hostingPlatform,
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully updated podcast.',
        });

        this.editProgramDialogData.visible = false;
        this.editProgramDialogData.program = null;

        this.getOrganizationsPrograms();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error updating podcast.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.editProgramDialogData.isSubmitting = false;
      }
    },
    onDeleteProgram(program) {
      this.deleteProgramDialogData = {
        ...this.deleteProgramDialogData,
        visible: true,
        body: `<p>Are you sure you want to delete this program: <strong>${program.name}</strong>?</p>
          <p>This action cannot be undone.</p>`,
        program,
      };
    },
    async onSubmitDeleteProgram() {
      try {
        this.deleteProgramDialogData.isSubmitting = true;

        await api.deleteProgram(this.deleteProgramDialogData.program.id);

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully deleted podcast.',
        });

        this.deleteProgramDialogData.visible = false;
        this.deleteProgramDialogData.program = null;

        this.getOrganizationsPrograms();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error deleting podcast.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.deleteProgramDialogData.isSubmitting = false;
      }
    },
    async onSubmitAddProgram({ programForm }) {
      try {
        this.addProgramDialogData.isSubmitting = true;

        const targetAgeRanges = programForm.targetAgeRanges.reduce((acc, item) => {
          acc[item] = true;

          return acc;
        }, {});

        await api.createProgram({
          organizationId: this.myUserStore.myOrganization.id,
          name: programForm.name,
          description: programForm.showDescription,
          contactName: programForm.hostName,
          contactEmail: programForm.hostEmail,
          averageListeners: programForm.averageMonthlyDownloads,
          tapProgramId: programForm.tapProgramId,
          spreakerShowId: programForm.spreakerShowId,
          targetGender: programForm.targetGender,
          targetAgeRanges,
          adPreferences: programForm.adPreferences,
          categories: programForm.categories,
          blacklistedCategories: programForm.categoriesBlacklist,
          approvalBehavior: programForm.approvalBehavior,
          rssUrl: programForm.rssUrl,
          containsSensitiveContent: programForm.containsSensitiveContent,
          hostUserId: programForm.hostUserId,
          hostingPlatform: programForm.hostingPlatform,
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully added podcast',
        });
        this.getOrganizationsPrograms();

        this.addProgramDialogData.visible = false;
      } catch (error) {
        const message = parseMessageFromError(error, 'Error adding podcast.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.addProgramDialogData.isSubmitting = false;
      }
    },
  },
};
</script>
